import { cn } from "@/utils";
import type { MetadataIcon } from "@cohesive/api-client";
import React from "react";
import { WorkspaceBrowser } from "../browser/WorkspaceBrowser";
import { WorkspaceProviders } from "../components/WorkspaceProviders";
import { useWorkspaceSelection } from "../hooks/useWorkspaceSelection";
import { HomeBrowser } from "./browser/HomeBrowser";
import { HomeComposer } from "./HomeComposer";
import { HomeGreeting } from "./HomeGreeting";
import { useHomeWideMode } from "./hooks/useHomeWideMode";

export interface HomeSurfaceProps {
  /** Force-filters the browser and the composer's chat target. */
  workspaceId?: string;
  workspaceName?: string;
  workspaceIcon?: MetadataIcon | null;
  /** Replaces <HomeGreeting/>. The workspace title row, when scoped. */
  title?: React.ReactNode;
  /** Leading card/row in the browser — upload ghosts on the workspace page. */
  browserLeadingSlot?: React.ReactNode;
  suppressedRecordIds?: Set<string>;
  /**
   * Mobile: scroll the whole surface as one column (greeting → composer →
   * browser) instead of pinning the chrome and scrolling only the browser body.
   * The surface drops its own height clamp so it grows to content; the caller
   * provides the scroll container. Desktop keeps the pinned-composer model.
   */
  pageScroll?: boolean;
}

/**
 * The shared home layout: greeting (or workspace title) → composer → resource
 * browser, in one centered column. `/` renders it unscoped; the workspace page
 * renders it with a `workspaceId`, which force-filters the browser and points
 * the composer at that workspace.
 *
 * Navigation between scopes is deliberately un-animated. The flash it was
 * meant to cover turned out to be a focus round trip (the rail stealing focus
 * on mousedown), fixed at the source in `RailWorkspaceExpandableRow`; a fade
 * on top of a swap that is already still just reads as lag.
 *
 * Mounts `WorkspaceProviders` (the selection context) so the browser's cards
 * and rows use the page click model — single click selects, double click
 * opens — on `/` and the workspace page alike.
 */
export function HomeSurface(props: HomeSurfaceProps = {}) {
  return (
    <WorkspaceProviders>
      <HomeSurfaceInner {...props} />
    </WorkspaceProviders>
  );
}

/** Below the provider so the surface root can clear selection on empty-space
 *  clicks (`clearIfOutside` keys off `[data-workspace-item]`). The root
 *  doesn't scroll — the greeting/composer/toolbar stay fixed and only the
 *  browser's item body scrolls (see `HomeBrowserList`). */
function HomeSurfaceInner({
  workspaceId,
  workspaceName,
  workspaceIcon,
  title,
  browserLeadingSlot,
  suppressedRecordIds,
  pageScroll = false,
}: HomeSurfaceProps) {
  const selection = useWorkspaceSelection();
  const [wideBrowser, setWideBrowser] = useHomeWideMode();

  // Each section carries the column constraint itself (rather than one shared
  // wrapper) so the browser alone can drop its max-width in wide mode while
  // the greeting and composer stay in the reading column.
  const columnClassName = "mx-auto w-full max-w-[58rem] px-4 sm:px-6 md:px-8";

  return (
    <div
      className={cn(
        "flex flex-col",
        // Page-scroll (mobile): no height clamp, so the column grows to content
        // and the caller's scroll container scrolls it — the inner browser
        // overflow becomes a no-op. Default: fill and clip, so only the browser
        // body scrolls and the greeting/composer stay pinned.
        pageScroll ? "min-h-full" : "h-full overflow-hidden",
      )}
      onClick={selection.clearIfOutside}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 pt-12 lg:pt-16">
        <div className={columnClassName}>{title ?? <HomeGreeting />}</div>
        <div className="flex min-h-0 flex-1 flex-col gap-10">
          <div className={columnClassName}>
            <HomeComposer
              workspaceId={workspaceId}
              workspaceName={workspaceName}
              workspaceIcon={workspaceIcon}
            />
          </div>
          <div
            /* Wide-mode stamp: `BROWSER_LIST_COL` gates the extra list
               columns (Type) on this ancestor attribute, so they exist only
               in full-width mode without prop-drilling `wide` into every
               header/row/folder-row renderer. */
            data-browser-wide={wideBrowser ? "" : undefined}
            className={cn(
              columnClassName,
              "flex min-h-0 flex-1 flex-col",
              // Wide mode drops the reading column but still caps at 1920px —
              // unbounded rows on an ultrawide put Name and Modified a head
              // turn apart.
              wideBrowser && "max-w-[1920px]",
            )}
          >
            {workspaceId ? (
              <WorkspaceBrowser
                workspaceId={workspaceId}
                leadingSlot={browserLeadingSlot}
                suppressedRecordIds={suppressedRecordIds}
                wide={wideBrowser}
                onWideChange={setWideBrowser}
              />
            ) : (
              <HomeBrowser
                leadingSlot={browserLeadingSlot}
                suppressedRecordIds={suppressedRecordIds}
                wide={wideBrowser}
                onWideChange={setWideBrowser}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
